/** Pure decrypt-mask helpers for {@link Ctextfx} (multi-origin bidirectional). */

function hashPlayKey(input: string): number {
  let hash = 2166136261;
  for (let index = 0; index < input.length; index++) {
    hash ^= input.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

function decryptOriginCount(length: number): number {
  if (length <= 1) return 1;
  if (length < 12) return 2;
  if (length < 36) return 3;
  return Math.min(5, 2 + Math.floor(length / 28));
}

/** Evenly spaced seeds with light playKey jitter — decrypt expands both ways from each. */
export function pickDecryptOrigins({
  length,
  playKey,
}: {
  length: number;
  playKey: string;
}): number[] {
  if (length <= 0) return [];
  if (length === 1) return [0];
  const count = decryptOriginCount(length);
  const used = new Set<number>();
  const origins: number[] = [];
  for (let originIndex = 0; originIndex < count; originIndex++) {
    const base = Math.floor(((originIndex + 0.5) / count) * length);
    const jitter = (hashPlayKey(`${playKey}:${originIndex}`) % 7) - 3;
    let origin = Math.max(0, Math.min(length - 1, base + jitter));
    let guard = 0;
    while (used.has(origin) && guard < length) {
      origin = (origin + 1) % length;
      guard += 1;
    }
    used.add(origin);
    origins.push(origin);
  }
  return origins.sort((left, right) => left - right);
}

export function maxDecryptRadius({
  length,
  origins,
}: {
  length: number;
  origins: readonly number[];
}): number {
  if (length <= 0 || origins.length === 0) return 0;
  let farthest = 0;
  for (let index = 0; index < length; index++) {
    let nearest = length;
    for (const origin of origins)
      nearest = Math.min(nearest, Math.abs(index - origin));
    farthest = Math.max(farthest, nearest);
  }
  return farthest;
}

export function buildDecryptRevealed({
  length,
  origins,
  radius,
}: {
  length: number;
  origins: readonly number[];
  radius: number;
}): boolean[] {
  return Array.from({ length }, (_, index) =>
    origins.some((origin) => Math.abs(index - origin) <= radius),
  );
}

export function prefixRevealed(length: number, count: number): boolean[] {
  return Array.from({ length }, (_, index) => index < count);
}
